import type { Swiper as SwiperInstance } from 'swiper';
import { ActivityDateText, BackgroundStyle } from 'components/commercetools-ui/organisms/curated-slider/types';
import { getMediaclipToken } from 'helpers/mediaclip/mediaclipToken';
import { PROJECT_EXPIRATION_PERIOD } from '../constants';

const UNITS = {
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
};

const getLabel = (count: number, singular: string, plural: string) => `${count} ${count === 1 ? singular : plural}`;

export const findExpirationDuration = (expiryISO: string, activityDateText?: ActivityDateText): string => {
  if (!expiryISO) return '';

  const labels = {
    day: activityDateText?.day ?? 'day',
    days: activityDateText?.days ?? 'days',

    hour: activityDateText?.hour ?? 'hour',
    hours: activityDateText?.hours ?? 'hours',

    minute: activityDateText?.minute ?? 'minute',
    minutes: activityDateText?.minutes ?? 'minutes',

    week: activityDateText?.week ?? 'week',
    weeks: activityDateText?.weeks ?? 'weeks',

    expired: activityDateText?.expired ?? 'Expired',
  };

  const expiryTime = new Date(expiryISO.length === 10 ? `${expiryISO}T23:59:59.999Z` : expiryISO).getTime();

  const diffMs = expiryTime - Date.now();
  if (diffMs <= 0) return labels.expired;

  if (diffMs < UNITS.HOUR) {
    const minutes = Math.max(1, Math.floor(diffMs / UNITS.MINUTE));
    return getLabel(minutes, labels.minute, labels.minutes);
  }

  if (diffMs < UNITS.DAY) {
    const hours = Math.floor(diffMs / UNITS.HOUR);
    const minutes = Math.floor((diffMs % UNITS.HOUR) / UNITS.MINUTE);
    return `${getLabel(hours, labels.hour, labels.hours)} & ${getLabel(minutes, labels.minute, labels.minutes)}`;
  }

  if (diffMs < UNITS.WEEK) {
    const days = Math.floor(diffMs / UNITS.DAY);
    const hours = Math.floor((diffMs % UNITS.DAY) / UNITS.HOUR);
    return `${getLabel(days, labels.day, labels.days)}, ${getLabel(hours, labels.hour, labels.hours)}`;
  }

  const weeks = Math.floor(diffMs / UNITS.WEEK);
  const days = Math.floor((diffMs % UNITS.WEEK) / UNITS.DAY);
  return `${getLabel(weeks, labels.week, labels.weeks)} & ${getLabel(days, labels.day, labels.days)}`;
};

export const decideImageOrColor = (
  backImage: boolean | undefined,
  backgroundEntity: string | undefined,
): BackgroundStyle => {
  if (backImage) {
    return {
      backgroundImage: `url(${backgroundEntity})`,
      backgroundSize: 'auto',
      backgroundPosition: 'center',
    };
  } else {
    return {
      backgroundColor: backgroundEntity,
    };
  }
};

export const orderedProjetsAsPerExpiry = (tiles: any) => {
  const finalTiles = tiles
    ?.filter((tile: any) => {
      if (!tile?.activityDate) return false;

      const startDate = new Date(tile.activityDate);
      const expiryDate = new Date(startDate);
      expiryDate.setDate(startDate.getDate() + PROJECT_EXPIRATION_PERIOD);

      const now = new Date();
      return expiryDate >= now;
    })
    .sort((a: any, b: any) => {
      const aExpiry = new Date(a.activityDate);
      aExpiry.setDate(aExpiry.getDate() + PROJECT_EXPIRATION_PERIOD);

      const bExpiry = new Date(b.activityDate);
      bExpiry.setDate(bExpiry.getDate() + PROJECT_EXPIRATION_PERIOD);

      return bExpiry.getTime() - aExpiry.getTime();
    });

  return finalTiles;
};

export const setFFIDsForPagination = (sw: SwiperInstance, ffidCurated: string) => {
  const base = `lnk-${ffidCurated}-pagination`;
  const el = sw.pagination?.el as HTMLElement | null | undefined;
  if (el) el.setAttribute('data-ffid', base);
};

export const formatDateMMMDDYYYY = (date: string | Date): string => {
  if (!date) return '';

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

/** @deprecated Use `getMediaclipToken` from `helpers/mediaclip/mediaclipToken` directly. */
export const getCachedToken = (forceRefresh = false) => getMediaclipToken(forceRefresh);

const GLOBAL_KEY = '__mediaclip_scheduler__';

type Scheduler = {
  lastRun: number;
  queue: Promise<any>;
};

export const getScheduler = (): Scheduler | null => {
  if (typeof window === 'undefined') return null;

  if (!(window as any)[GLOBAL_KEY]) {
    (window as any)[GLOBAL_KEY] = {
      lastRun: 0,
      queue: Promise.resolve(),
    };
  }

  return (window as any)[GLOBAL_KEY];
};

export const runBatchWithControl = async (batchFn: () => Promise<any>) => {
  const scheduler = getScheduler();
  if (!scheduler) return;

  scheduler.queue = scheduler.queue.then(async () => {
    const now = Date.now();
    const diff = now - scheduler.lastRun;

    if (diff < 60000) {
      await new Promise((res) => setTimeout(res, 60000 - diff));
    }

    scheduler.lastRun = Date.now();

    return batchFn();
  });

  return scheduler.queue;
};
